import GuestLayout from "@/components/layout-guest";
import { useGuestData } from "@/hooks/use-guest";
import { useRoute } from "wouter";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2, ChevronRight, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/use-language";
import { api, buildUrl } from "@shared/routes";
import type { Section } from "@shared/schema";

type Step = 'info' | 'rules' | 'confirm';

export default function GuestCheckin() {
  const [match, params] = useRoute("/g/:slug/checkin");
  const slug = params?.slug || "";
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("t");
  const { lang } = useLanguage();

  const { data, isLoading } = useGuestData(slug, token);
  const { toast } = useToast();

  const [step, setStep] = useState<Step>('info');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState(false);

  const [formData, setFormData] = useState({
    guestName: "",
    guestPhone: "",
    guestEmail: "",
    notes: "",
  });

  const [rulesAccepted, setRulesAccepted] = useState(false);

  if (isLoading || !data) return <div className="p-8 text-center">Loading...</div>;

  const { apartment, sections } = data;

  const ruleSections = (sections as Section[]).filter(
    (s: Section) => s.category === "rules"
  );

  const canAdvanceInfo = formData.guestName.trim() && formData.guestPhone.trim();

  const handleSubmit = async () => {
    if (!token) return;
    setIsSubmitting(true);
    try {
      const url = buildUrl(api.guestCheckin.submit.path, { slug }) + `?t=${token}`;
      const body = {
        guestName: formData.guestName,
        guestPhone: formData.guestPhone,
        guestEmail: formData.guestEmail || undefined,
        rulesAccepted: true,
        notes: formData.notes || undefined,
      };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (res.status === 409) {
        setAlreadyCheckedIn(true);
        setStep('confirm');
        return;
      }

      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.message || "Failed to submit check-in");
      }

      setStep('confirm');
    } catch (err: any) {
      const msg = err.message || "";
      const isNoReservation = msg.includes("No active reservation");
      toast({
        title: lang === 'en' ? "Check-in Unavailable" : "Check-in no disponible",
        description: isNoReservation
          ? (lang === 'en'
            ? "No active reservation found for today. Please contact your host."
            : "No se encontro una reservacion activa para hoy. Contacte a su anfitrion.")
          : (lang === 'en'
            ? msg || "Please check your internet connection and try again."
            : "Por favor verifique su conexion e intente de nuevo."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = {
    info: (
      <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300">
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-bold" data-testid="text-step-title">
            {lang === 'en' ? "Your Details" : "Tus Datos"}
          </h2>
          <p className="text-muted-foreground">
            {lang === 'en' ? "Please provide your contact info." : "Por favor proporcione su informacion de contacto."}
          </p>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{lang === 'en' ? "Full Name *" : "Nombre Completo *"}</Label>
            <Input
              value={formData.guestName}
              onChange={e => setFormData(p => ({ ...p, guestName: e.target.value }))}
              placeholder={lang === 'en' ? "John Doe" : "Juan Perez"}
              data-testid="input-guest-name"
            />
          </div>
          <div className="space-y-2">
            <Label>{lang === 'en' ? "Phone Number *" : "Numero de Telefono *"}</Label>
            <Input
              value={formData.guestPhone}
              onChange={e => setFormData(p => ({ ...p, guestPhone: e.target.value }))}
              placeholder="+1 234 567 8900"
              data-testid="input-guest-phone"
            />
          </div>
          <div className="space-y-2">
            <Label>{lang === 'en' ? "Email (Optional)" : "Correo (Opcional)"}</Label>
            <Input
              value={formData.guestEmail}
              onChange={e => setFormData(p => ({ ...p, guestEmail: e.target.value }))}
              placeholder="john@example.com"
              data-testid="input-guest-email"
            />
          </div>
        </div>
        <Button
          className="w-full btn-primary h-12"
          disabled={!canAdvanceInfo}
          onClick={() => setStep('rules')}
          data-testid="button-next-info"
        >
          {lang === 'en' ? "Next Step" : "Siguiente"} <ChevronRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    ),
    rules: (
      <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300">
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-bold" data-testid="text-rules-title">
            {lang === 'en' ? "House Rules" : "Reglas de la Casa"}
          </h2>
          <p className="text-muted-foreground">
            {lang === 'en'
              ? "Please read and accept the house rules before checking in."
              : "Por favor lea y acepte las reglas de la casa antes de registrarse."}
          </p>
        </div>
        <div className="space-y-4">
          {ruleSections.length > 0 ? (
            ruleSections.map((section: Section) => {
              const title = lang === 'en' ? section.titleEn : section.titleEs;
              const content = lang === 'en' ? section.contentEn : section.contentEs;
              return (
                <Card key={section.id} data-testid={`card-rule-${section.id}`}>
                  <CardContent className="p-4 space-y-2">
                    <p className="font-medium text-sm" data-testid={`text-rule-title-${section.id}`}>{title}</p>
                    {content && (
                      <div
                        className="text-sm text-muted-foreground prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: content }}
                        data-testid={`text-rule-content-${section.id}`}
                      />
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4" data-testid="text-no-rules">
              {lang === 'en' ? "No house rules available." : "No hay reglas de la casa disponibles."}
            </p>
          )}
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{lang === 'en' ? "Additional Notes (Optional)" : "Notas Adicionales (Opcional)"}</Label>
            <Textarea
              value={formData.notes}
              onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
              placeholder={lang === 'en' ? "Any special requests or notes..." : "Cualquier solicitud especial o notas..."}
              className="resize-none"
              data-testid="input-notes"
            />
          </div>
          <div className="flex items-start gap-3 p-4 border rounded-md bg-card">
            <Checkbox
              id="accept-rules"
              checked={rulesAccepted}
              onCheckedChange={(checked) => setRulesAccepted(checked === true)}
              className="mt-0.5"
              data-testid="checkbox-accept-rules"
            />
            <label
              htmlFor="accept-rules"
              className="text-sm leading-snug cursor-pointer"
            >
              {lang === 'en'
                ? "I have read and accept the house rules."
                : "He leido y acepto las reglas de la casa."}
            </label>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 h-12" onClick={() => setStep('info')} data-testid="button-back-rules">
            {lang === 'en' ? "Back" : "Atras"}
          </Button>
          <Button
            className="flex-1 btn-primary h-12"
            disabled={!rulesAccepted || isSubmitting}
            onClick={handleSubmit}
            data-testid="button-submit-checkin"
          >
            {isSubmitting ? <Loader2 className="animate-spin" /> : (lang === 'en' ? "Complete Check-in" : "Completar Registro")}
          </Button>
        </div>
      </div>
    ),
    confirm: (
      <div className="flex flex-col items-center justify-center py-12 text-center animate-in zoom-in-95 duration-500">
        {alreadyCheckedIn ? (
          <>
            <div className="w-20 h-20 bg-orange-100 dark:bg-orange-950 text-orange-600 dark:text-orange-400 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-display font-bold mb-2" data-testid="text-already-checked-in">
              {lang === 'en' ? "Already Checked In" : "Ya Registrado"}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xs mx-auto" data-testid="text-already-checked-in-desc">
              {lang === 'en'
                ? "You have already completed check-in for this reservation."
                : "Ya ha completado el registro para esta reservacion."}
            </p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-green-100 dark:bg-green-950 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-display font-bold mb-2" data-testid="text-checkin-success">
              {lang === 'en' ? "Check-in Complete!" : "Registro Completado!"}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xs mx-auto" data-testid="text-checkin-success-desc">
              {lang === 'en'
                ? "Welcome! You have successfully checked in. Enjoy your stay!"
                : "Bienvenido! Se ha registrado exitosamente. Disfrute su estancia!"}
            </p>
          </>
        )}
        <Button variant="outline" onClick={() => window.location.href = `/g/${slug}?t=${token}`} data-testid="button-back-home">
          {lang === 'en' ? "Back to Home" : "Volver al Inicio"}
        </Button>
      </div>
    ),
  };

  return (
    <GuestLayout slug={slug} title={lang === 'en' ? "Check In" : "Registro"} showNav={false}>
      {step !== 'confirm' && (
        <div className="flex gap-2 mb-8" data-testid="stepper-progress">
          {['info', 'rules'].map((s, i) => {
            const currentIdx = ['info', 'rules'].indexOf(step);
            const isActive = i <= currentIdx;
            return (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-colors ${isActive ? 'bg-primary' : 'bg-muted'}`}
              />
            );
          })}
        </div>
      )}

      {steps[step]}
    </GuestLayout>
  );
}
